import { FC } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router'

import { PostQuoteRoutes } from './post-quote'
import { DirectFlow, PostQuoteEdit } from 'directFlow/App'
import { EmailCapture } from 'directFlow/pages/EmailCapture/EmailCapture'
import { StartQuoteRedirect } from 'directFlow/pages/StartQuoteRedirect'
import { Page } from 'directFlow/types'
import { OpenBankingAuthentication } from 'postQuote/features/payments/OpenBankingAuthentication'
import { ExclusionWrapper } from 'components/Exclusion/ExclusionWrapper'
import { PCWExclusion } from 'components/Exclusion/PCWExclusion'
import { WebExclusion } from 'components/Exclusion/WebExclusion'
import { Header, PostQuoteHeader } from 'components/Header'
import { StartNewQuote } from 'components/StartNewQuote'

import { Channel } from 'types'
import { ErrorPage } from 'src/Error/ErrorPage'
import { NotFound } from 'src/Error/NotFound'
import { TimeoutPage } from 'src/Error/TimeoutPage'
import { directRoutes, postQuoteEditRoutes } from 'src/router/direct-flow/routeConfig'
import { ValidateDirectRoutes } from 'src/router/direct-flow/ValidateDirectRoutes/ValidateDirectRoutes'
import { miscsRoutes, postQuoteRoutes } from 'src/router/post-quote/routeConfig'
import { report } from 'utils'

interface RouterProps {
  existingQuoteId?: string
}

export const Router: FC<RouterProps> = ({ existingQuoteId }) => {
  return (
    <BrowserRouter basename="/car-insurance-quote">
      <ErrorBoundary
        fallback={<ErrorPage />}
        onError={(error, info) => {
          report(error, 'Router error boundary', { args: { info } })
        }}
      >
        <Routes>
          <Route path="/start-quote" element={<StartNewQuote />} />
          <Route
            path="/"
            element={
              existingQuoteId ? (
                <Navigate to={`/direct/${existingQuoteId}/start`} />
              ) : (
                <Navigate to="/start-quote" />
              )
            }
          />
          <Route path="/open-banking-authentication" element={<OpenBankingAuthentication />} />
          <Route path="/quote/timeout" element={<TimeoutPage />} />
          <Route
            path="/quote/:quoteId/exclusion"
            element={
              <ExclusionWrapper>
                {(exclusionState) => {
                  if (exclusionState.channel === Channel.WEB) {
                    return <WebExclusion {...exclusionState} />
                  }
                  return <PCWExclusion {...exclusionState} />
                }}
              </ExclusionWrapper>
            }
          />
          <Route path="/direct/:quoteId" element={<DirectFlow />}>
            <Route path="/direct/:quoteId/start" element={<StartQuoteRedirect />} />
            <Route path="/direct/:quoteId/save-for-later" element={<EmailCapture />} />
            <Route path="/direct/:quoteId/error" element={<ErrorPage />} />
            {directRoutes
              .filter((route) => route.name !== Page.EDIT_DETAILS)
              .map((route, index) => (
                <Route
                  key={index}
                  path={route.path}
                  element={
                    <ValidateDirectRoutes routeName={route.name} isDirect>
                      {route.component}
                    </ValidateDirectRoutes>
                  }
                />
              ))}
            <Route path="*" element={<NotFound />} />
          </Route>

          <Route path="/edit/:quoteId" element={<PostQuoteEdit />}>
            {postQuoteEditRoutes.map((route, index) => (
              <Route key={index} path={route.path} element={route.component} />
            ))}
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route path="/quote/:quoteId" element={<PostQuoteRoutes />}>
            {postQuoteRoutes.map(({ path, component, hideProgressBar }) => {
              return (
                <Route
                  key={path}
                  path={path}
                  element={
                    <>
                      <PostQuoteHeader hideProgressBar={hideProgressBar} />
                      {component}
                    </>
                  }
                />
              )
            })}

            {miscsRoutes.map(({ path, component }) => (
              <Route
                key={path}
                path={path}
                element={
                  <>
                    <Header />
                    {component}
                  </>
                }
              />
            ))}

            <Route
              path="*"
              element={
                <>
                  <Header />
                  <NotFound />
                </>
              }
            />
          </Route>

          <Route path="/error" element={<ErrorPage />} />
          <Route path="/not-found" element={<NotFound />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </ErrorBoundary>
    </BrowserRouter>
  )
}
